/*
 * Description:
 * ---------------------
 * The access editor, sliding in from the right. Allow access composes access:
 * From, the boxes whose devices connect (the box it was opened for ticked
 * already); To, the box a device is at and the device (the one it was opened
 * for picked already), from what its box sees, every device on its network,
 * or typed by its address; and, before saving, the exact path from each box
 * picked, drawn as on the map, with the address it would use, filled in and
 * changeable, and why that address works there. Each box ticked says it lets
 * every device there connect, never some of them. A device already shared keeps
 * who connects to it now and gains the boxes picked. Manage device access is
 * the same for one device: who may connect, each on its own path with its
 * setup, its name, the advanced settings, or stop sharing it. Each box picked
 * is its own permission. Sharing everything on a network where devices are
 * shared on their own says so first and takes them over on save. Saving goes
 * through the panel, which owns every call to the server. Wording lives in
 * reachWords.js.
 *
 * ! file components/ShareSheet.js
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { TbRouter } from "react-icons/tb";
import useBoxReads from "../hooks/useBoxReads";
import * as W from "./reachWords";

const SEARCH_FROM = 8;
// The device picked: one its box sees is keyed by its address.
const NET = "net";
const TYPED = "typed";

const takesPart = (s) => W.readiness(s) !== "update";

export default function ShareSheet({ mode, start = {}, entry, sites, entries, view, busy, failure, onSave, onRemove, onClose }) {
  const editing = mode === "edit";
  // Managing one device: its own access, edited, or a device not shared yet, opened for itself.
  const manage = editing || !!start.manage;
  const { source } = start;
  const byId = useMemo(() => Object.fromEntries(sites.map((s) => [s.node_id, s])), [sites]);
  const [holder, setHolder] = useState(() => (editing ? entry.site
    : start.holder || sites.find((s) => s.node_id !== source && takesPart(s))?.node_id || ""));
  const [chosen, setChosen] = useState(() => start.pick || "");
  const [name, setName] = useState(() => (editing ? entry.name : start.name || ""));
  const [target, setTarget] = useState(() => (editing ? entry.lan_address : start.target || ""));
  const [keepSource, setKeepSource] = useState(() => !!entry?.keep_source);
  const [fleet, setFleet] = useState(() => entry?.fleet_address || "");
  const [query, setQuery] = useState("");
  // Each box: ticked or not, and its address once set; unset follows the suggestion.
  const [picks, setPicks] = useState(() => (editing
    ? Object.fromEntries(entry.reached_from.map((c) => [c.site, { on: true, address: c.address || "" }]))
    : source ? { [source]: { on: takesPart(byId[source] || {}) } } : {}));
  const ref = useRef(null);
  useEffect(() => ref.current?.focus(), []);

  const { reads } = useBoxReads(editing ? [] : [holder], "/diagnostics/devices");
  const read = reads[holder];
  const lan = W.lanOf(byId[holder]);
  const { rows, net } = W.hereRows(entries, holder, lan, read?.body?.devices, read ? !!read.body : undefined);
  // The device it was opened for, until its box answers with the devices it sees.
  const opened = start.pick && ![NET, TYPED].includes(start.pick) && holder === start.holder && !rows.some((r) => r.key === start.pick)
    ? { key: start.pick, ip: start.target, name: start.name } : null;
  const existing = editing ? entry : W.entryFor(entries, holder, target);
  const adding = !editing && !!existing;
  const fixed = adding ? existing.reached_from : [];
  const covered = !editing && !existing && chosen === NET ? W.coveredBy(entries, holder, target) : [];

  const candidates = sites.filter((s) => s.node_id !== holder);
  // Managing a device lists the boxes it has been opened with or given, and adds others one at a time.
  const listed = candidates.filter((s) => s.node_id in picks);
  const addable = candidates.filter((s) => !(s.node_id in picks));
  const shown = manage ? listed : candidates.length >= SEARCH_FROM && query
    ? candidates.filter((s) => s.node_id.toLowerCase().includes(query.trim().toLowerCase()))
    : candidates;
  const isFixed = (id) => fixed.some((c) => c.site === id);
  const ticked = candidates.filter((s) => picks[s.node_id]?.on && !isFixed(s.node_id));
  const open = candidates.filter((s) => takesPart(s) && !isFixed(s.node_id));
  const allOn = open.every((s) => picks[s.node_id]?.on);
  const suggest = (id) => W.suggestAddress(target, W.lanOf(byId[id]), view, id, existing);
  const addressOf = (id) => picks[id]?.address ?? suggest(id);
  const fleetNow = adding ? existing.fleet_address : fleet.trim();

  const set = (id, change) => setPicks((p) => ({ ...p, [id]: { ...p[id], ...change } }));
  const tickAll = () => setPicks((p) => ({ ...p, ...Object.fromEntries(open.map((s) => [s.node_id, { ...p[s.node_id], on: !allOn }])) }));
  const pickHolder = (id) => {
    setHolder(id);
    setChosen("");
    setTarget("");
    setName("");
  };
  const choose = (key) => {
    setChosen(key);
    if (key === NET) {
      setTarget(lan);
      setName(net ? net.name : `Everything at ${holder}`);
      // Who reaches devices there now keeps reaching them through the network.
      const callers = W.coveredBy(entries, holder, lan).flatMap((e) => e.reached_from.map((c) => c.site));
      setPicks((p) => ({ ...p, ...Object.fromEntries(callers.map((id) => [id, { on: true }])) }));
      return;
    }
    const r = rows.find((x) => x.key === key);
    setTarget(r ? r.ip : "");
    setName(r ? r.name : "");
  };

  const select = ticked.some((s) => s.node_id === source) ? source : ticked[0]?.node_id;
  const canSave = holder && target.trim() && name.trim() && (editing || ticked.length > 0);
  const save = () => onSave({
    method: existing ? "PUT" : "POST",
    path: existing ? `/reach/${existing.id}` : "/reach",
    replaces: covered,
    id: existing?.id,
    select,
    body: {
      name: (adding ? existing.name : name).trim(),
      site: holder,
      lan_address: (adding ? existing.lan_address : target).trim(),
      fleet_address: fleetNow,
      reached_from: [
        ...fixed.map((c) => ({ site: c.site, address: c.address || "" })),
        ...ticked.map((s) => ({ site: s.node_id, address: String(addressOf(s.node_id) || "").trim() })),
      ],
      keep_source: adding ? !!existing.keep_source : keepSource,
      disabled: !!existing?.disabled,
    },
  });

  const nameField = (
    <label className="rd-field">
      <span>Name in the list</span>
      <input className="dl-input" value={name} maxLength={64} onChange={(e) => setName(e.target.value)}
        placeholder="Betting terminal, shop 12" aria-label="Name" />
    </label>
  );
  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="rd-sheet" role="dialog" aria-modal="true" aria-labelledby="rd-sheet-title" ref={ref} tabIndex={-1}
        onKeyDown={(e) => e.key === "Escape" && onClose()}>
        <header className="rd-sheet-head">
          <div className="rd-sheet-heading">
            {editing && <span className="rd-sheet-eyebrow">at {holder}</span>}
            <h2 id="rd-sheet-title">{W.sheetTitle(mode, entry, start)}</h2>
            <p>{W.sheetSubtitle(mode, { name, holder, target, manage })}</p>
          </div>
          <button type="button" className="rd-sheet-close" onClick={onClose} aria-label="Close"><FiX size={18} /></button>
        </header>

        <div className="rd-sheet-body">
          <section className="rd-step" aria-labelledby="rd-from-title">
            <div className="rd-step-head">
              <h3 id="rd-from-title">{manage ? W.ACCESS_FROM : W.FROM_TITLE}</h3>
              {!manage && open.length > 2 && <button type="button" className="dl-btn rd-small" onClick={tickAll}>{allOn ? "None" : W.SELECT_ALL}</button>}
            </div>
            {!manage && candidates.length >= SEARCH_FROM && (
              <input className="dl-input rd-find" value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Find a box" aria-label="Find a box" />
            )}
            {!candidates.length && <p className="dl-note">{W.NONE_TO_SHARE}</p>}
            {manage && candidates.length > 0 && !listed.length && <p className="dl-note">{W.NO_SITE_YET}</p>}
            <ul className="rd-picks">
              {shown.map((s) => {
                const id = s.node_id;
                const out = !takesPart(s);
                const known = existing?.reached_from.some((c) => c.site === id);
                const setup = known ? W.setupOf(existing, id, view.anchors) : null;
                const on = !!picks[id]?.on || isFixed(id);
                const offline = W.readiness(s) === "offline" ? " · offline, picks it up when back" : "";
                return (
                  <li key={id} className={`rd-pick${on ? " is-on" : ""}${out ? " is-out" : ""}`} aria-labelledby={`rd-pick-${id}`}>
                    <label className="rd-pick-main">
                      <input type="checkbox" checked={on} disabled={isFixed(id) || (out && !picks[id]?.on)}
                        onChange={(e) => set(id, { on: e.target.checked })} aria-label={`Allow connections from ${id}`} />
                      <TbRouter size={15} aria-hidden="true" />
                      <span className="rd-pick-name" id={`rd-pick-${id}`}>{id}</span>
                      {setup && <span className={`rd-setup is-${setup.state}`}>{W.SETUP_WORDS[setup.state]}</span>}
                      {on && !out
                        ? <span className="rd-pick-who">{W.whoConnects(id, W.lanOf(s))}{offline}</span>
                        : <span className="rd-pick-meta">{out ? W.READINESS.update : W.lanOf(s)}{offline}</span>}
                    </label>
                  </li>
                );
              })}
            </ul>
            {manage && addable.length > 0 && (
              <select className="dl-input rd-add-site" value="" aria-label={W.ADD_SITE}
                onChange={(e) => e.target.value && set(e.target.value, { on: true })}>
                <option value="">{W.ADD_SITE}</option>
                {addable.map((s) => (
                  <option key={s.node_id} value={s.node_id} disabled={!takesPart(s)}>
                    {s.node_id}{takesPart(s) ? "" : ` (${W.READINESS.update})`}
                  </option>
                ))}
              </select>
            )}
          </section>

          {!manage && (
            <section className="rd-step" aria-labelledby="rd-to-title">
              <h3 id="rd-to-title">{W.TO_TITLE}</h3>
              <div className="rd-field-row">
                <label className="rd-field">
                  <span>{W.BOX}</span>
                  <select className="dl-input" value={holder} onChange={(e) => pickHolder(e.target.value)} aria-label="Box the device is at">
                    <option value="">{W.CHOOSE_BOX}</option>
                    {sites.filter(takesPart).map((s) => <option key={s.node_id} value={s.node_id}>{s.node_id}</option>)}
                  </select>
                </label>
                <label className="rd-field">
                  <span>{W.PICK_DEVICE}</span>
                  <select className="dl-input" value={chosen} disabled={!holder} onChange={(e) => choose(e.target.value)} aria-label={W.PICK_DEVICE}>
                    <option value="">{W.CHOOSE_DEVICE}</option>
                    {opened && <option value={opened.key}>{opened.name === opened.ip ? opened.ip : `${opened.name} (${opened.ip})`}</option>}
                    {!net && rows.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.name === r.ip ? r.ip : `${r.name} (${r.ip})`}{r.entry ? ` · ${W.ALREADY_SHARED}` : ""}
                      </option>
                    ))}
                    {(net || W.isShareableNetwork(lan)) && (
                      <option value={NET}>{W.everyDeviceAt(holder, lan)}{net ? ` · ${W.ALREADY_SHARED}` : ""}</option>
                    )}
                    {!net && <option value={TYPED}>{W.TYPE_ADDRESS}</option>}
                  </select>
                </label>
              </div>
              {holder && !read && <p className="dl-note">{W.SITE_READING}</p>}
              {read && !read.body && <p className="dl-note">{W.SITE_SILENT}</p>}
              {read?.body && !rows.length && <p className="dl-note">{W.SITE_EMPTY}</p>}
              {net && <p className="dl-note">{W.netCovers(holder)}</p>}
              {adding && <p className="dl-note">{W.addingTo(existing)}</p>}
              {chosen === NET && !net && <p className="dl-note">{W.SHARE_NETWORK_MEANS}</p>}
              {chosen === TYPED && (
                <div className="rd-field-row">
                  {!existing && nameField}
                  <label className="rd-field">
                    <span>{W.LOCAL_IP}</span>
                    <input className="dl-input rd-mono" value={target} onChange={(e) => setTarget(e.target.value)}
                      placeholder="192.168.0.1" aria-label="Its address at that box" />
                  </label>
                </div>
              )}
              {chosen === TYPED && holder && <p className="dl-note rd-hint">{W.lanHint(byId[holder])}</p>}
              {chosen && chosen !== TYPED && !existing && nameField}
              {covered.length > 0 && (
                <p className="rd-takeover" role="note"><FiAlertTriangle size={15} aria-hidden="true" /><span>{W.replaceWords(covered)}</span></p>
              )}
            </section>
          )}

          {target.trim() && ticked.length > 0 && (
            <section className="rd-step" aria-labelledby="rd-preview-title">
              <h3 id="rd-preview-title">{W.PREVIEW_TITLE}</h3>
              <ul className="rd-paths">
                {ticked.map((s) => {
                  const id = s.node_id;
                  const known = existing?.reached_from.some((c) => c.site === id);
                  return (
                    <PathPreview key={id} box={id} holder={holder} name={(adding ? existing.name : name).trim() || target.trim()}
                      target={target.trim()} address={addressOf(id)} fleet={fleetNow} changing={picks[id]?.changing}
                      why={W.addressWhy(target.trim(), holder, id, s, addressOf(id), rows, fleetNow)}
                      setup={known ? W.setupOf(existing, id, view.anchors) : null}
                      onChange={() => set(id, { changing: true, address: addressOf(id) })} onAddress={(a) => set(id, { address: a })} />
                  );
                })}
              </ul>
              <p className="dl-note">{W.SCOPE_NOTE}</p>
            </section>
          )}

          {manage && nameField}

          {!adding && (
            <details className="dl-measure rd-more">
              <summary>{W.ADVANCED}</summary>
              <label className="dl-check">
                <input type="checkbox" checked={keepSource} onChange={(e) => setKeepSource(e.target.checked)} />
                {W.KEEP_SOURCE_LABEL}
              </label>
              <p className="dl-note">{W.KEEP_SOURCE_MEANS}</p>
              <label className="rd-field">
                <span>{W.FLEET_LABEL}</span>
                <input className="dl-input rd-mono" value={fleet} onChange={(e) => setFleet(e.target.value)}
                  placeholder="Picked by Weavian" aria-label={W.FLEET_LABEL} />
              </label>
              <p className="dl-note">{W.fleetMeans(view.pool)}</p>
              {editing && (
                <div className="rd-details">
                  {W.detailLines(entry).map((line) => <span key={line}>{line}</span>)}
                </div>
              )}
            </details>
          )}
          {failure && <p className="dl-error" role="alert">{failure}</p>}
        </div>

        <footer className="rd-sheet-foot">
          {editing && (
            <button type="button" className="dl-btn danger rd-stop" disabled={busy} onClick={() => onRemove(entry)}>{W.STOP_SHARING}</button>
          )}
          <button type="button" className="dl-btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="dl-btn primary" onClick={save} disabled={busy || !canSave}>
            {busy ? "Saving…" : W.allowButton(manage ? "edit" : mode, ticked.length, !!target.trim())}
          </button>
        </footer>
      </aside>
    </>
  );
}

/* One permission before it is saved, drawn as on the canvas: the box whose
   devices connect, the address on the path, the device at its box. */
function PathPreview({ box, holder, name, target, address, fleet, changing, why, setup, onChange, onAddress }) {
  const shown = W.pathAddress(target, address, fleet);
  return (
    <li className="rd-pv" aria-label={W.previewSentence(box, name, holder, target, String(address || "").trim() || fleet)}>
      <div className="rd-pv-path">
        <span className="rd-pv-from"><TbRouter size={15} aria-hidden="true" /><span><small>{W.ALL_DEVICES_AT}</small>{box}</span></span>
        <span className="rd-pv-link">
          {changing ? (
            <input className="dl-input rd-mono" value={address} autoFocus onChange={(e) => onAddress(e.target.value)}
              placeholder="Empty: Weavian picks" aria-label={`Connection address for ${box}`} />
          ) : (
            <span className={`rd-ac-pill rd-mono${shown.address ? "" : " is-auto"}`}>{shown.address || shown.note}</span>
          )}
          <span className="rd-ac-tail" aria-hidden="true" />
        </span>
        <span className="rd-pv-to">
          <small>{holder}</small>
          <strong>{name}</strong>
          <span>{W.isRange(target) ? W.everyDevice(target) : <>{W.LOCAL_IP} <span className="rd-mono">{target}</span></>}</span>
        </span>
      </div>
      <div className="rd-pv-more">
        {setup && <span className={`rd-setup is-${setup.state}`}>{W.SETUP_WORDS[setup.state]}</span>}
        {!changing && <button type="button" className="rd-link" onClick={onChange} aria-label={`Change the address ${box} uses`}>{W.CHANGE}</button>}
        {shown.address && shown.note && <span className="rd-pv-note">{shown.note}</span>}
      </div>
      {changing && <p className="dl-note rd-hint">{W.ADDRESS_HELP}</p>}
      {why.text && (why.tone === "warn"
        ? <p className="rd-warn" role="note"><FiAlertTriangle size={14} /> <span>{why.text}</span></p>
        : <p className="dl-note rd-hint">{why.text}</p>)}
    </li>
  );
}
